import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  Stack,
  Alert,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import Papa from 'papaparse';
import { Dropzone } from '@/components';
import { CreateProductInput, useCreateProductsBatchMutation } from '@/services/product-catalogue/pc-api';
import { useAppSelector } from '@/store';
import { roles } from '@/roles';
import { downloadCSVTemplate } from '../csv-template/template-generator';
import CSVColumnMapper, { ColumnMapping } from '../csv-column-mapper';
import BatchProductReview from '../batch-product-review';
import useSnackbar from '@/hooks/useSnackbar';
import { ModalV2 } from '@/components';

interface CSVBatchUploadProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (createdProductIds?: string[]) => void;
  onTriggerBulkPolling?: (productIds: string[]) => void;
}

enum UploadStep {
  UPLOAD = 0,
  MAP = 1,
  REVIEW = 2,
}

const STEPS = ['Upload CSV', 'Map Columns', 'Review & Submit'];

const CSVBatchUpload: React.FC<CSVBatchUploadProps> = ({ open, onClose, onSuccess, onTriggerBulkPolling }) => {
  const [activeStep, setActiveStep] = useState<UploadStep>(UploadStep.UPLOAD);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<any[]>([]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [mappedProducts, setMappedProducts] = useState<CreateProductInput[]>([]);
  const [selectedProducts, setSelectedProducts] = useState<boolean[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const { show: toast } = useSnackbar();
  const [createBatch, { isLoading: isSubmitting }] = useCreateProductsBatchMutation();
  
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const { user } = useAppSelector((state) => state.auth);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setActiveStep(UploadStep.UPLOAD);
      setCsvFile(null);
      setCsvData([]);
      setCsvHeaders([]);
      setMappedProducts([]);
      setSelectedProducts([]);
      setIsProcessing(false);
    }
  }, [open]);

  const handleFileUpload = useCallback((file: File) => {
    if (!file) return;

    setCsvFile(file);
    setIsProcessing(true);

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        if (results.errors.length > 0) {
          toast('Error parsing CSV file', results.errors[0].message, 'error');
          setIsProcessing(false);
          return;
        }

        if (results.data.length === 0) {
          toast('CSV file is empty', 'Please upload a file with data', 'error');
          setIsProcessing(false);
          return;
        }

        if (results.data.length > 100) {
          toast('Too many products', 'Maximum 100 products allowed per upload', 'error');
          setIsProcessing(false);
          return;
        }

        setCsvHeaders(results.meta.fields || []);
        setCsvData(results.data as any[]);
        setActiveStep(UploadStep.MAP);
        setIsProcessing(false);
        
        toast(`Successfully parsed ${results.data.length} products`, '', 'success');
      },
      error: (error) => {
        toast('Failed to parse CSV', error.message, 'error');
        setIsProcessing(false);
      }
    });
  }, [toast]);

  const handleMappingChange = useCallback((_mapping: ColumnMapping) => {
    // Column mapping is handled by the mapper component itself
  }, []);

  const handleValidDataChange = useCallback((validData: CreateProductInput[]) => {
    setMappedProducts(validData);
    setSelectedProducts(new Array(validData.length).fill(true));
  }, []);

  const handleNextFromMapping = () => {
    if (mappedProducts.length === 0) {
      toast('No valid products found', 'Please check your column mapping and data', 'error');
      return;
    }
    setActiveStep(UploadStep.REVIEW);
  };



  const handleProductsUpdate = (updatedProducts: CreateProductInput[]) => {
    setMappedProducts(updatedProducts);
  };

  const handleSelectedProductsChange = (selected: boolean[]) => {
    setSelectedProducts(selected);
  };

  const handleReviewSubmit = async () => {
    try {
      const selectedProductsData = mappedProducts.filter((_, index) => selectedProducts[index]);
      
      // Check if TD Admin with TRADEDIVE_CLIENT tenant
      const isTDAdminTCD = user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
      
      // Get client ID - properly handle different user roles
      const clientId = (() => {
        if (isTDAdminTCD) {
          return selectedTenant?._id;
        }
        if (selectedClient?._id) {
          return selectedClient._id;
        }
        if (user?.userRole === roles.TDCLIENT || user?.userRole === roles.SPCLIENT) {
          return user?.tenantId;
        }
        return undefined;
      })();
      
      // Add client/user information to each product
      const productsWithContext = selectedProductsData.map(product => ({
        ...product,
        // Always pass clientId; TD Client uses their tenantId as clientId
        ...(clientId ? { clientId } : {}),
        // For TD Admin, also pass tenantId
        ...(isTDAdminTCD && { tenantId: selectedTenant?._id }),
      }));

      const result = await createBatch({
        products: productsWithContext
      }).unwrap();

      const createdProductIds = result.createdProducts.map(p => p._id);
      
      if (result.success) {
        toast(`Successfully created ${result.summary.totalCreated} products!`, '', 'success');
        onSuccess(createdProductIds);
      } else {
        const message = `Created ${result.summary.totalCreated} products, ${result.summary.totalFailed} failed`;
        toast(message, '', result.summary.totalFailed > 0 ? 'warning' : 'success');
        
        if (result.errors.length > 0) {
          console.error('Batch creation errors:', result.errors);
        }
        
        onSuccess(createdProductIds);
      }
      
      // Trigger bulk polling for created products
      if (onTriggerBulkPolling && createdProductIds.length > 0) {
        onTriggerBulkPolling(createdProductIds);
      }
    } catch (error) {
      console.error('Batch upload error:', error);
      toast('Failed to upload products. Please try again.', '', 'error');
    }
  };

  // Step-based modal configurations
  const getModalTitle = () => {
    switch (activeStep) {
      case UploadStep.UPLOAD:
        return 'Upload Product CSV';
      case UploadStep.MAP:
        return 'Map CSV Columns';
      case UploadStep.REVIEW:
        return 'Review & Submit Products';
      default:
        return 'Bulk Upload Products';
    }
  };

  const getModalSubtitle = () => {
    switch (activeStep) {
      case UploadStep.UPLOAD:
        return 'Upload a CSV file with your product data. Maximum 100 products per upload.';
      case UploadStep.MAP:
        return 'Map your CSV columns to product fields. Required fields are marked with *.';
      case UploadStep.REVIEW:
        return 'Review your products and fix any validation issues before submitting.';
      default:
        return '';
    }
  };

  const getButtonText = () => {
    switch (activeStep) {
      case UploadStep.UPLOAD:
        return 'Next: Map Columns';
      case UploadStep.MAP:
        return `Review Products (${mappedProducts.length})`;
      case UploadStep.REVIEW:
        return `Upload ${mappedProducts.length} Products`;
      default:
        return 'Next';
    }
  };

  const handlePrimaryAction = () => {
    switch (activeStep) {
      case UploadStep.UPLOAD:
        // For upload step, the button should be disabled until file is uploaded
        // File upload automatically proceeds to next step
        break;
      case UploadStep.MAP:
        handleNextFromMapping();
        break;
      case UploadStep.REVIEW:
        handleReviewSubmit();
        break;
    }
  };

  const canProceed = () => {
    switch (activeStep) {
      case UploadStep.UPLOAD:
        return csvData.length > 0;
      case UploadStep.MAP:
        return mappedProducts.length > 0;
      case UploadStep.REVIEW:
        const selectedCount = selectedProducts.filter(Boolean).length;
        const hasErrors = mappedProducts.some(product => {
          // Basic validation - check required fields
          return !product.sku || !product.name || !product.htsCode || !product.countryOfOrigin;
        });
        return selectedCount > 0 && !hasErrors && !isSubmitting;
      default:
        return false;
    }
  };

  const renderUploadStep = () => (
    <Box>
      <Paper sx={{ p: 4, textAlign: 'center', mb: 3 }}>
        <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h5" gutterBottom>
          Upload Product CSV
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Upload a CSV file with your product data. Maximum 100 products per upload.
        </Typography>

        <Box sx={{ maxWidth: 500, mx: 'auto', mb: 3 }}>
          <Dropzone
            onUpload={handleFileUpload}
            accept={['text/csv', 'application/vnd.ms-excel']}
            extensions={['csv']}
            fileFormat="CSV"
            maxSize={5 * 1024 * 1024} // 5MB
            disabled={isProcessing}
          />
        </Box>

        {isProcessing && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Processing CSV file...
            </Typography>
          </Box>
        )}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Need a Template?
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Download our CSV template to get started with the correct format and see examples.
        </Typography>
        
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => downloadCSVTemplate(true)}
          >
            Download with Examples
          </Button>
          <Button
            variant="outlined"
            startIcon={<DescriptionIcon />}
            onClick={() => downloadCSVTemplate(false)}
          >
            Download Empty Template
          </Button>
        </Stack>

        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>Required fields:</strong> Product SKU, Product Name, HTS Code, Country of Origin<br />
            <strong>Optional fields:</strong> Description, Metal Percentage (%)<br />
            <strong>Format tips:</strong> Use 2-letter country codes (US, CN, MX) and format HTS codes as XXXX.XX.XXXX
          </Typography>
        </Alert>
      </Paper>
    </Box>
  );

  const renderMappingStep = () => (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Map CSV Columns
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Map your CSV columns to product fields. We've auto-detected some mappings for you.
        </Typography>
        
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            <strong>File:</strong> {csvFile?.name} ({csvData.length} rows)
          </Typography>
        </Alert>
      </Paper>

      <CSVColumnMapper
        csvHeaders={csvHeaders}
        csvData={csvData}
        onMappingChange={handleMappingChange}
        onValidDataChange={handleValidDataChange}
      />
    </Box>
  );

  const renderReviewStep = () => (
    <BatchProductReview
      products={mappedProducts}
      onUpdate={handleProductsUpdate}
      selectedProducts={selectedProducts}
      onSelectedProductsChange={handleSelectedProductsChange}
    />
  );

  const renderStepContent = () => {
    switch (activeStep) {
      case UploadStep.UPLOAD:
        return renderUploadStep();
      case UploadStep.MAP:
        return renderMappingStep();
      case UploadStep.REVIEW:
        return renderReviewStep();
      default:
        return null;
    }
  };

  return (
    <ModalV2
      show={open}
      setShow={onClose}
      title={getModalTitle()}
      subTitle={getModalSubtitle()}
      buttonText={getButtonText()}
      buttonAction={handlePrimaryAction}
      buttonLoader={!canProceed()}
      isConfirmButtonLoading={activeStep === UploadStep.REVIEW ? isSubmitting : false}
      maxWidth="xl"
      modalStyles={{
        "& .MuiPaper-root": {
          maxWidth: "1200px",
        },
      }}
    >
      <Box>
        {/* Stepper */}
        <Box sx={{ mt: 3, mb: 4 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {STEPS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
        
        {/* Step Content */}
        {renderStepContent()}
      </Box>
    </ModalV2>
  );
};

export default CSVBatchUpload;